"use client";
import {
  CarouselContent,
  CarouselItem,
  Carousel,
  type CarouselApi,
} from "@/components/atoms/carousel";
import Icon from "@/components/atoms/Icons";
import Heading from "@/components/molecules/Heading";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { InfoCarrosselBlockData } from "@/page-builder/fragments/InfoCarrosselBlock/types";
import clsx from "clsx";
import Image from "@/components/atoms/image";
import { useTranslations } from "next-intl";
import { useState } from "react";

export const CarouselModule = ({
  card = [],
  title = "",
}: InfoCarrosselBlockData) => {
  const t = useTranslations("Carousel");
  const [api, setApi] = useState<CarouselApi | null>(null);

  const scrollPrev = () => {
    api?.scrollPrev();
  };

  const scrollNext = () => {
    api?.scrollNext();
  };

  const { isDesktop, isMobile, isTablet } = useMediaQuery();

  return (
    <div className="flex w-full overflow-hidden">
      <div className="container mx-auto">
        <div className="flex flex-col">
          <Heading title={title} />

          <div className="mt-32 relative ">
            <Carousel
              setApi={setApi}
              opts={{ align: "start" }}
              className="w- relative"
            >
              <CarouselContent overflow="visible" className="items-center ">
                {card.map((item, i) => {
                  return (
                    <CarouselItem
                      key={i}
                      className={clsx(
                        `group h-full basis-[80vw] relative md:basis-[50vw] xl:basis-[54%] flex flex-col overflow-hidden xl:flex-row mr-[24px] min-h-[484px] md:min-h-[400px] xl:min-h-[360px] ` +
                          (i % 2 == 0 ? "bg-secondary-300" : "bg-primary-200")
                      )}
                    >
                      <div className="relative w-full h-[156px] md:h-[156px] xl:h-auto xl:w-[48%]">
                        {item.desktopImage?.url && (
                            <Image
                              src={
                                isDesktop
                                  ? item.desktopImage?.url
                                  : isTablet
                                  ? item.tabletImage?.url
                                  : isMobile
                                  ? item.mobileImage?.url
                                  : item.desktopImage?.url
                              }
                              alt={
                                isDesktop
                                  ? item.desktopImage?.alt
                                  : isTablet
                                  ? item.tabletImage?.alt
                                  : isMobile
                                  ? item.mobileImage?.alt
                                  : item.desktopImage?.alt
                              }
                              fill
                              className="object-cover md:object-left object-top"
                            />
                          )}
                        <div className="absolute inset-0 z-20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                      </div>

                      {i % 2 === 0 ? (
                        <div
                          className={`absolute z-10 xl:top-0 top-[22%] md:top-[30%] md:flex ${
                            isDesktop ? "right-0" : "-right-[40%]"
                          } w-auto h-[165px] xl:h-[300px]`}
                          aria-hidden="true"
                        >
                          <Icon name="fig10" />
                        </div>
                      ) : (
                        <>
                          <div
                            className="absolute z-10 top-0 md:flex right-[-4px] h-auto w-[80%] md:w-[60%] xl:w-[60%]"
                            aria-hidden="true"
                          >
                            <Icon name="fig11" />
                          </div>
                          <div
                            className="absolute z-10 bottom-0 hidden xl:flex right-[0px] h-auto w-auto"
                            aria-hidden="true"
                          >
                            <Icon name="fig12" />
                          </div>
                        </>
                      )}

                      <div className="p-[24px] flex-1 z-10 flex flex-col gap-[12px] justify-start xl:justify-end xl:mt-auto h-full">
                        <p className="b-0 text-neutral-900 text-xl-bold xl:text-2xl-bold leading-9 xl:leading-[40px] md:leading-[28px] ">
                          {item.title}
                        </p>
                        <p className="b-0 text-neutral-900 text-regular leading-[28px] ">
                          {item.description}
                        </p>
                      </div>
                    </CarouselItem>
                  );
                })}
              </CarouselContent>

              <div className="flex gap-[16px] mt-24 justify-between md:justify-start">
                <button
                  type="button"
                  title={t("previous")}
                  aria-label={t("previous")}
                  onClick={scrollPrev}
                  className="group flex items-center justify-center w-[52px] h-[52px] rounded-full border border-primary-600 hover:border-primary-700 hover:bg-primary-700 transition-colors"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="text-primary-600 group-hover:text-white transition-colors"
                  >
                    <path d="M19 11H7.83L12.71 6.12C13.1 5.73 13.1 5.09 12.71 4.7C12.32 4.31 11.69 4.31 11.3 4.7L4.71 11.29C4.32 11.68 4.32 12.31 4.71 12.7L11.3 19.29C11.69 19.68 12.32 19.68 12.71 19.29C13.1 18.9 13.1 18.27 12.71 17.88L7.83 13H19C19.55 13 20 12.55 20 12C20 11.45 19.55 11 19 11Z" />
                  </svg>
                </button>

                <button
                  type="button"
                  title={t("next")}
                  aria-label={t("next")}
                  onClick={scrollNext}
                  className="group flex items-center justify-center w-[52px] h-[52px] rounded-full border border-primary-600 hover:border-primary-700 hover:bg-primary-700 transition-colors"
                >
                  <svg
                    className="rotate-180 text-primary-600 group-hover:text-white transition-colors"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M19 11H7.83L12.71 6.12C13.1 5.73 13.1 5.09 12.71 4.7C12.32 4.31 11.69 4.31 11.3 4.7L4.71 11.29C4.32 11.68 4.32 12.31 4.71 12.7L11.3 19.29C11.69 19.68 12.32 19.68 12.71 19.29C13.1 18.9 13.1 18.27 12.71 17.88L7.83 13H19C19.55 13 20 12.55 20 12C20 11.45 19.55 11 19 11Z" />
                  </svg>
                </button>
              </div>
            </Carousel>
          </div>
        </div>
      </div>
    </div>
  );
};
